import { legalDefaults } from "@/content/legal";
import { getSiteConfig } from "@/lib/site-config";

export interface LegalSection {
  heading: string;
  body: string;
}

export interface LegalDocument {
  title: string;
  lastUpdated: string;
  sections: LegalSection[];
}

export interface LegalContent {
  privacyPolicy: LegalDocument;
  termsOfService: LegalDocument;
}

function cloneSections(sections: readonly LegalSection[]): LegalSection[] {
  return sections.map((section) => ({ ...section }));
}

export const DEFAULT_LEGAL_CONTENT: LegalContent = {
  privacyPolicy: {
    title: legalDefaults.privacyPolicy.title,
    lastUpdated: legalDefaults.privacyPolicy.lastUpdated,
    sections: cloneSections(legalDefaults.privacyPolicy.sections),
  },
  termsOfService: {
    title: legalDefaults.termsOfService.title,
    lastUpdated: legalDefaults.termsOfService.lastUpdated,
    sections: cloneSections(legalDefaults.termsOfService.sections),
  },
};

function nonEmpty(value: string | undefined | null, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}

function mergeSections(
  partial: readonly LegalSection[] | undefined,
  defaults: readonly LegalSection[]
): LegalSection[] {
  if (!partial?.length) {
    return cloneSections(defaults);
  }

  const merged = defaults.map((defaultSection, index) => {
    const item = partial[index];
    if (!item) return { ...defaultSection };
    return {
      heading: nonEmpty(item.heading, defaultSection.heading),
      body: nonEmpty(item.body, defaultSection.body),
    };
  });

  if (partial.length > defaults.length) {
    for (let index = defaults.length; index < partial.length; index += 1) {
      const item = partial[index];
      const heading = item?.heading?.trim() ?? "";
      const body = item?.body?.trim() ?? "";
      if (heading && body) {
        merged.push({ heading, body });
      }
    }
  }

  return merged.length > 0 ? merged : cloneSections(defaults);
}

export function mergeLegalDocument(
  partial: Partial<LegalDocument> | undefined,
  defaults: LegalDocument
): LegalDocument {
  const sectionsPartial = partial?.sections;
  const hasStoredSections =
    Array.isArray(sectionsPartial) && sectionsPartial.length > 0;

  return {
    title: nonEmpty(partial?.title, defaults.title),
    lastUpdated: nonEmpty(partial?.lastUpdated, defaults.lastUpdated),
    sections: hasStoredSections
      ? sectionsPartial
          .map((section) => ({
            heading: section.heading?.trim() ?? "",
            body: section.body?.trim() ?? "",
          }))
          .filter((section) => section.heading && section.body)
      : mergeSections(sectionsPartial, defaults.sections),
  };
}

export function mergeLegalContent(
  partial?: Partial<LegalContent> | null
): LegalContent {
  return {
    privacyPolicy: mergeLegalDocument(
      partial?.privacyPolicy,
      DEFAULT_LEGAL_CONTENT.privacyPolicy
    ),
    termsOfService: mergeLegalDocument(
      partial?.termsOfService,
      DEFAULT_LEGAL_CONTENT.termsOfService
    ),
  };
}

/** Inject live site contact email into the Privacy Policy Contact section. */
export function withPrivacyContactEmail(
  sections: readonly LegalSection[]
): LegalSection[] {
  const email = getSiteConfig().contact.email;

  return sections.map((section) =>
    isPrivacyContactHeading(section.heading)
      ? {
          ...section,
          body: `For privacy-related questions, contact us at ${email}.`,
        }
      : { ...section }
  );
}

/** Match CMS variants like "Contact" / "Contact us". */
export function isPrivacyContactHeading(heading: string): boolean {
  const normalized = heading.trim().toLowerCase().replace(/\s+/g, " ");
  return normalized === "contact" || normalized.startsWith("contact ");
}

/** Static fallback legal content for public pages (includes contact email sync). */
export function getLegalContentFallback(): LegalContent {
  return {
    privacyPolicy: {
      ...DEFAULT_LEGAL_CONTENT.privacyPolicy,
      sections: withPrivacyContactEmail(DEFAULT_LEGAL_CONTENT.privacyPolicy.sections),
    },
    termsOfService: {
      ...DEFAULT_LEGAL_CONTENT.termsOfService,
      sections: cloneSections(DEFAULT_LEGAL_CONTENT.termsOfService.sections),
    },
  };
}

/** Merge API/CMS data with defaults and apply public-only contact email sync. */
export function resolvePublicLegalContent(partial?: Partial<LegalContent> | null): LegalContent {
  const merged = mergeLegalContent(partial);
  return {
    ...merged,
    privacyPolicy: {
      ...merged.privacyPolicy,
      sections: withPrivacyContactEmail(merged.privacyPolicy.sections),
    },
  };
}

/** @deprecated Use mergeLegalContent() or resolvePublicLegalContent() */
export function getLegalContent(): LegalContent {
  return getLegalContentFallback();
}
